// Artefact anchors (AB#350): a named target put on a text range (prose, slide text) or
// on a whole slide element, that a link of the same artefact can point at (`#anchor-<id>`).
//
// An anchor is `{ id, name }`. The id is generated by whoever creates the anchor and never changes,
// so a rename keeps every link to it working; a link goes by id only. The name is what a member reads
// in the link window: required, at most ANCHOR_NAME_MAX characters, whitespace collapsed, and unique
// within the artefact case-insensitively (the editors enforce the uniqueness — it spans rooms).
//
// Where an anchor lives:
//   slides  `Run.anchor` (letters) and `ElementSnap.anchor` (a whole element) — domain/slides.ts
//   prose   a bracketed span in the part's markdown, in the colour span's family (text-colour.ts):
//
//             [words]{anchor=<id> name="Pricing summary"}
//
//           the name between double quotes, `"` and `\` escaped with a backslash. It is never a link
//           (a link's `]` is followed by `(`), and its opener is the fixed `[`, like the colour's.
//
// PARITY. backend/domain/anchors.ts is the same module for the server (the web cannot import backend
// source); both assert against shared/anchors.fixture.json. `newAnchorId` is the web's own.

/** A named anchor. */
export interface Anchor {
  id: string
  name: string
}

/** An anchor id: the same shape as a slide or element id. */
export const ANCHOR_ID_RE = /^[A-Za-z0-9_-]{1,64}$/
/** The longest anchor name. */
export const ANCHOR_NAME_MAX = 80
/** A default name (the selected text) is cut to about this many characters. */
export const ANCHOR_DEFAULT_NAME_LEN = 40
/** The string form of a link to an anchor: `#anchor-<id>`. */
export const ANCHOR_HREF_PREFIX = '#anchor-'

/** A name as stored: every run of whitespace one space, trimmed. */
export const normalizeAnchorName = (name: string): string => name.replace(/\s+/g, ' ').trim()

/** The key two names collide on (the uniqueness is case-insensitive). */
export const anchorNameKey = (name: string): string => normalizeAnchorName(name).toLocaleLowerCase()

/** Why an anchor is invalid (a short human message), or null when it is valid. */
export function anchorProblem(a: Anchor): string | null {
  if (typeof a.id !== 'string' || !ANCHOR_ID_RE.test(a.id)) return `'${String(a.id)}' is not an anchor id`
  if (typeof a.name !== 'string' || normalizeAnchorName(a.name) === '') return 'an anchor needs a name'
  if (a.name !== normalizeAnchorName(a.name)) return 'an anchor name has no leading, trailing or repeated spaces'
  if (a.name.length > ANCHOR_NAME_MAX) return `an anchor name is at most ${ANCHOR_NAME_MAX} characters`
  return null
}

/**
 * A stored anchor, read tolerantly: `{ id, name }` with a valid id and a non-empty name (normalised,
 * cut to ANCHOR_NAME_MAX); else none. Returns a clean object.
 */
export function readAnchor(v: unknown): Anchor | undefined {
  if (typeof v !== 'object' || v === null) return undefined
  const r = v as Record<string, unknown>
  if (typeof r.id !== 'string' || typeof r.name !== 'string') return undefined
  const a = { id: r.id, name: normalizeAnchorName(r.name).slice(0, ANCHOR_NAME_MAX).trim() }
  return anchorProblem(a) === null ? a : undefined
}

/** Same anchor (same id and name); two absent anchors are the same. */
export function sameAnchor(a: Anchor | undefined, b: Anchor | undefined): boolean {
  if (a === undefined || b === undefined) return a === b
  return a.id === b.id && a.name === b.name
}

/** The default name of an anchor on `text`: its words, cut to about ANCHOR_DEFAULT_NAME_LEN characters. */
export function defaultAnchorName(text: string): string {
  const t = normalizeAnchorName(text)
  if (t.length <= ANCHOR_DEFAULT_NAME_LEN) return t
  const cut = t.slice(0, ANCHOR_DEFAULT_NAME_LEN + 1)
  const space = cut.lastIndexOf(' ')
  return (space >= ANCHOR_DEFAULT_NAME_LEN / 2 ? cut.slice(0, space) : t.slice(0, ANCHOR_DEFAULT_NAME_LEN)).trim()
}

/** The link to an anchor, in its string form. */
export const anchorHref = (id: string): string => `${ANCHOR_HREF_PREFIX}${id}`

/** The anchor id an href points at (`#anchor-<id>`), or null when it is not an anchor link. */
export function anchorIdOfHref(href: string): string | null {
  const v = href.trim()
  if (!v.startsWith(ANCHOR_HREF_PREFIX)) return null
  const id = v.slice(ANCHOR_HREF_PREFIX.length)
  return ANCHOR_ID_RE.test(id) ? id : null
}

// ── the prose markdown form ─────────────────────────────────────────────────────────────────────

/** A name between the quotes of the markdown form: `\` and `"` escaped. */
const quoteName = (name: string): string => name.replace(/[\\"]/g, (c) => `\\${c}`)

/** The closing delimiter of an anchored span: `]{anchor=<id> name="…"}`. */
export const anchorClose = (a: Anchor): string => `]{anchor=${a.id} name="${quoteName(a.name)}"}`

/** The attribute block right after the `]` of an anchored span. */
const ANCHOR_ATTR_RE = /^\{anchor=([A-Za-z0-9_-]{1,64}) name="((?:[^"\\\n]|\\.)*)"\}/

/** Past a code span opening at `i` (a run of backticks), or just past the run when it never closes. */
function skipCode(src: string, i: number, max: number): number {
  let run = 1
  while (src[i + run] === '`') run++
  const end = src.indexOf('`'.repeat(run), i + run)
  return end >= 0 && end + run <= max ? end + run : i + run
}

export interface AnchorMatch {
  /** The content is `src.slice(from, close)`. */
  from: number
  close: number
  /** Just past the `{anchor=…}`. */
  end: number
  anchor: Anchor
}

/**
 * The anchored span opening at `at` (`[content]{anchor=<id> name="…"}`), or null. Brackets nest,
 * escapes and code spans are skipped (the colour span's scan, text-colour.ts). A malformed attribute
 * block or an invalid anchor is no span. The web's markdown-it rule is the same scan.
 */
export function matchAnchor(src: string, at: number, max = src.length): AnchorMatch | null {
  if (src[at] !== '[') return null
  const from = at + 1
  let depth = 1
  let close = -1
  for (let i = from; i < max; ) {
    const c = src[i]
    if (c === '\\') {
      i += 2
      continue
    }
    if (c === '`') {
      i = skipCode(src, i, max)
      continue
    }
    if (c === '[') depth++
    else if (c === ']' && --depth === 0) {
      close = i
      break
    }
    i++
  }
  if (close <= from) return null
  const attr = ANCHOR_ATTR_RE.exec(src.slice(close + 1, max))
  if (!attr) return null
  const anchor = readAnchor({ id: attr[1], name: attr[2]!.replace(/\\(.)/g, '$1') })
  if (!anchor) return null
  return { from, close, end: close + 1 + attr[0].length, anchor }
}

/** Every anchor of a prose part's markdown, in order (fenced code skipped). */
export function proseAnchors(md: string): Array<Anchor & { text: string; offset: number }> {
  const out: Array<Anchor & { text: string; offset: number }> = []
  let fence: string | null = null
  let offset = 0
  for (const line of md.split('\n')) {
    const f = /^\s*(`{3,}|~{3,})/.exec(line)?.[1]
    if (f && (fence === null || (f[0] === fence[0] && f.length >= fence.length))) fence = fence === null ? f : null
    else if (fence === null && line.includes(']{anchor=')) {
      for (let i = 0; i < line.length; ) {
        if (line[i] === '\\') {
          i += 2
          continue
        }
        if (line[i] === '`') {
          i = skipCode(line, i, line.length)
          continue
        }
        const m = line[i] === '[' ? matchAnchor(line, i) : null
        // Nested anchors are found too: scan on inside the span, not past it.
        if (m) out.push({ ...m.anchor, text: line.slice(m.from, m.close), offset: offset + i })
        i++
      }
    }
    offset += line.length + 1
  }
  return out
}

/** A fresh anchor id: 10 random letters and digits (the editors create anchors, never the server). */
export function newAnchorId(): string {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789'
  const bytes = new Uint8Array(10)
  crypto.getRandomValues(bytes)
  return 'a' + Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('').slice(1)
}
